import React, { FunctionComponent, useMemo, useState } from "react";
import { Grid, GridSize, ListItemIcon, Menu, MenuItem, Skeleton } from "@mui/material";
import { IArtist, useGetArtists } from "../../../../hooks/artists";
import { strings } from "../../../../localization/LocalizedStrings";
import { ModifyArtistDialog } from "../ModifyArtistDialog";
import { ArtistCard } from "../ArtistCard";
import { ArtistDialog } from "../ArtistDialog";
import { AttributeDesktop } from "../../AttributeDesktop";
import { toast } from "react-toastify";
import { useContextMenu } from "../../../../hooks/contextMenu";
import styles from "./styles.module.scss";
import { attributeIconMap } from "../../../../models/AttributeUtilities";
import { AttributeType } from "../../AttributeList";
import { Icon, IconType } from "../../../../icons";
import { useResettableDialog } from "../../../../hooks/resettableDialog/useResettableDialog";

export const ArtistsDesktop: FunctionComponent<IArtistsDesktopProps> = ({
  handleClose,
  isPublic,
  columnWidth,
  preselectedId,
  onArtistSelected,
  onClearValue
}) => {
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [artists, resetArtists, setArtists, noMoreArtists] = useGetArtists(isPublic, setLoading);
  const filteredArtists = useMemo(() => {
    if (searchText == "") {
      return artists;
    }
    return artists?.filter(artist => {
      return artist.name.toLowerCase().includes(searchText.toLowerCase());
    });
  }, [artists, searchText]);
  const {
    state: modifyDialogState,
    key: modifyDialogKey,
    openAdd,
    openEdit,
    close: closeModifyDialog
  } = useResettableDialog<IArtist>();
  const {
    state: artistDialogState,
    key: artistDialogKey,
    openEdit: openArtistDialog,
    close: closeArtistDialog
  } = useResettableDialog<IArtist>();
  const [artistImageData, setArtistImageData] = useState<string>();
  const [contextArtist, setContextArtist] = useState<IArtist>();
  const { menuProps, handleContextMenu, handleMenuClose } = useContextMenu();
  const onModifiedArtist = (newArtist: IArtist) => {
    const newArtists = artists.map(artist => (artist.uuid === newArtist.uuid ? newArtist : artist));
    newArtists.sort((a, b) => a.name.localeCompare(b.name, undefined, { ignorePunctuation: true }));
    setArtists(newArtists);
    toast.success(strings.generic_update_success);
  };
  const onDeletedArtist = (deletedArtist: IArtist) => {
    const newArtists = artists.filter(artist => artist.uuid !== deletedArtist.uuid);
    setArtists(newArtists);
    toast.success(strings.generic_update_success);
  };
  return (
    <AttributeDesktop
      handleClose={handleClose}
      isPublic={isPublic}
      items={filteredArtists}
      emptyIconType={attributeIconMap[AttributeType.ARTISTS]}
      emptyString={isPublic ? strings.empty_artists_public : strings.empty_artists}
      addItemString={strings.add_attribute}
      title={strings.attribute_artists_title}
      loading={loading}
      setLoading={setLoading}
      onSearchRequested={searchText => setSearchText(searchText)}
      addAction={() => {
        setArtistImageData(undefined);
        openAdd();
      }}
      refreshAction={resetArtists}
      onClearValue={onClearValue}
    >
      {filteredArtists?.map(artist => (
        <Grid item key={artist.uuid} xs={columnWidth}>
          <ArtistCard
            artist={artist}
            selected={preselectedId == artist.uuid}
            onClick={async (_event, imageData) => {
              setArtistImageData(imageData);
              if (onArtistSelected) {
                onArtistSelected(artist);
              } else {
                openEdit(artist);
              }
            }}
            onContextMenu={async (event, imageData) => {
              setArtistImageData(imageData);
              setContextArtist(artist);
              handleContextMenu(event);
            }}
          />
        </Grid>
      ))}
      {(loading || !noMoreArtists) &&
        Array.from({ length: 20 }, (_value, index) => (
          <Grid key={index} item xs={columnWidth}>
            <Skeleton variant={"rectangular"} sx={{ height: 90 }} className={styles.skeleton} />
          </Grid>
        ))}
      <ModifyArtistDialog
        key={modifyDialogKey}
        open={modifyDialogState.mode !== "closed"}
        onClose={closeModifyDialog}
        artist={modifyDialogState.item}
        originalImageData={artistImageData}
        onNewItem={newArtist => {
          const newArtists = [...artists, newArtist];
          newArtists.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { ignorePunctuation: true })
          );
          setArtists(newArtists);
          toast.success(strings.generic_update_success);
        }}
        onModifiedItem={onModifiedArtist}
        onDeletedItem={onDeletedArtist}
      />
      <Menu {...menuProps}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openArtistDialog(contextArtist);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.FrameInspect} />
          </ListItemIcon>
          {strings.artist_view}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            openEdit(contextArtist);
          }}
          dense
        >
          <ListItemIcon>
            <Icon type={IconType.EditSquare} />
          </ListItemIcon>
          {strings.artist_edit}
        </MenuItem>
      </Menu>
      {artistDialogState.item && (
        <ArtistDialog
          key={artistDialogKey}
          artist={artistDialogState.item}
          isOpen={artistDialogState.mode !== "closed"}
          onClose={closeArtistDialog}
          canEdit={true}
          onModifiedItem={onModifiedArtist}
          onDeletedItem={deletedArtist => {
            closeArtistDialog();
            onDeletedArtist(deletedArtist);
          }}
        />
      )}
    </AttributeDesktop>
  );
};

export interface IArtistsDesktopProps {
  handleClose?: () => void;
  isPublic: boolean;
  columnWidth: GridSize;
  preselectedId?: string;
  onArtistSelected?: (artist: IArtist) => void;
  onClearValue?: () => void;
}
